import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import { Folder, RefreshCw, Clipboard, Check, Eye, Plus, Target, Pen, Trash } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import StatCard from '@/components/common/StatCard/StatCard';
import CategoryFormModal from "@/components/common/CategoryFormModal/CategoryFormModal";

const AdvancedCategoriesPanel = () => {
  const { success, error } = useNotifications();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [draggedCategory, setDraggedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/categories/detailed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (categoryData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });

      if (response.ok) {
        success('Categoría creada exitosamente');
        fetchCategories();
        setShowCreateForm(false);
      } else {
        const error = await response.json();
        success(`Error: ${error.detail}`);
      }
    } catch (error) {
      success('Error creando categoría');
    }
  };

  const handleUpdateCategory = async (categoryId, updateData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        success('Categoría actualizada exitosamente');
        fetchCategories();
        setEditingCategory(null);
      } else {
        const error = await response.json();
        success(`Error: ${error.detail}`);
      }
    } catch (error) {
      success('Error actualizando categoría');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (window.confirm(`¿Estás seguro de eliminar la categoría "${categoryName}"? Esta acción puede no ser reversible.`)) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/categories/${categoryId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          success('Categoría procesada exitosamente');
          fetchCategories();
        } else {
          const error = await response.json();
          success(`Error: ${error.detail}`);
        }
      } catch (error) {
        success('Error eliminando categoría');
      }
    }
  };

  const handleReorder = async (newOrder) => {
    try {
      const token = localStorage.getItem('token');
      const orderData = newOrder.map((cat, index) => ({
        id: cat._id,
        order: index
      }));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/categories/reorder-advanced`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        setCategories(newOrder);
        success('Orden actualizado exitosamente');
      }
    } catch (error) {
      success('Error reordenando categorías');
    }
  };

  const handleDragStart = (e, category) => {
    setDraggedCategory(category);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', category._id);

    // Agregar clase visual al elemento siendo arrastrado
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedCategory(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetCategory) => {
    e.preventDefault();

    if (!draggedCategory || draggedCategory._id === targetCategory._id) {
      return;
    }

    const newOrder = [...categories];
    const draggedIndex = newOrder.findIndex(c => c._id === draggedCategory._id);
    const targetIndex = newOrder.findIndex(c => c._id === targetCategory._id);

    // Remover elemento arrastrado
    const [removed] = newOrder.splice(draggedIndex, 1);

    // Insertar en nueva posición
    newOrder.splice(targetIndex, 0, removed);

    // Actualizar estado local inmediatamente
    setCategories(newOrder);

    // Enviar al backend
    handleReorderAPI(newOrder);
    setDraggedCategory(null);
  };

  const handleReorderAPI = async (newOrder) => {
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;

      for (let i = 0; i < newOrder.length; i++) {
        const category = newOrder[i];

        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/categories/${category._id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              display_order: i
            })
          });

          if (response.ok) {
            successCount++;
          } else {
            console.error(`❌ Error actualizando ${category.name}:`, response.status);
          }
        } catch (error) {
          console.error(`❌ Error en ${category.name}:`, error.message);
        }
      }

      if (successCount > 0) {
        // Recargar para ver cambios
        setTimeout(() => {
          fetchCategories();
        }, 500);
      } else {
        console.error('❌ No se pudo actualizar ninguna categoría');
        success('❌ Error guardando el orden');
        fetchCategories();
      }

    } catch (error) {
      console.error('❌ Error completo:', error);
      success(`❌ Error: ${error.message}`);
      fetchCategories();
    }
  };

  if (loading) {
    return <LoadingSpinner message="Cargando las categorías..." />;
  }

  return (
    <div className="space-y-8">
      {/* Header con estadísticas */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8">
        <h1 className="text-3xl font-bold mb-4">Gestión de Categorías</h1>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard title="Total Categorías" value={categories.length} icon={<Folder />} />
          <StatCard title="Activas" value={categories.filter(c => c.is_active).length} icon={<Check />} />
          <StatCard title="Con Trámites" value={categories.filter(c => c.tramite_count > 0).length} icon={<Clipboard />} />
          <StatCard title="Solicitudes Activas" value={categories.reduce((sum, c) => sum + c.active_requests_count, 0)} icon={<RefreshCw />} />
        </div>
      </div>

      {/* Controles */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Categorías del Sistema</h2>
          <p className="text-gray-600">Arrastra para reordenar • Gestión completa con estadísticas</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium flex row items-center"
        >
          <Plus className="mr-1" /> Nueva Categoría
        </button>
      </div>

      {/* Lista de categorías con drag & drop */}
      <div className="space-y-4">
        {categories.map((category, index) => (
          <CategoryAdvancedCard
            key={category._id}
            category={category}
            index={index}
            onEdit={() => setEditingCategory(category)}
            onDelete={() => handleDeleteCategory(category._id, category.name)}
            onDragStart={(e) => handleDragStart(e, category)}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, category)}
            isDragged={draggedCategory?._id === category._id}
          />
        ))}
      </div>

      {/* Modals */}
      {showCreateForm && (
        <CategoryFormModal
          onClose={() => setShowCreateForm(false)}
          onSave={handleCreateCategory}
        />
      )}

      {editingCategory && (
        <CategoryFormModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={(data) => handleUpdateCategory(editingCategory._id, data)}
        />
      )}
    </div>
  );
};

const CategoryAdvancedCard = ({ category, index, onEdit, onDelete, onDragStart, onDragEnd, onDragOver, onDrop, isDragged }) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDraggedOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Solo salir si realmente salimos del elemento
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggedOver(false);
    }
  };

  const handleDropWrapper = (e) => {
    e.preventDefault();
    setIsDraggedOver(false);
    onDrop(e);
  };

  return (
    <div
      draggable={true}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDropWrapper}
      className={`bg-white border border-gray-200 rounded-xl shadow-lg border-2 p-6 hover:shadow-xl transition-all duration-300 cursor-move select-none
        ${isDragged
          ? 'opacity-50 scale-95 border-blue-400'
          : isDraggedOver
            ? 'border-blue-500 bg-blue-50 scale-105 shadow-2xl opacity-100'
            : 'border-gray-200 hover:border-blue-300 opacity-100'
        }`}
      style={{
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none'
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-4 flex-1">
          {/* Handle de drag más prominente */}
          <div className="flex flex-col items-center cursor-grab active:cursor-grabbing">
            <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mb-1 hover:bg-blue-200 transition-colors">
              {index + 1}
            </div>
          </div>

          {/* Icono de categoría */}
          <div className="text-4xl pointer-events-none">{category.icon}</div>

          {/* Información básica */}
          <div className="flex-1 pointer-events-none">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-xl font-bold text-gray-900">{category.name}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${category.color}-100 text-${category.color}-800`}>
                {category.color}
              </span>
              {!category.is_active && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Inactiva
                </span>
              )}
              {category.specialization_required && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Target /> Especialista
                </span>
              )}
            </div>

            <p className="text-gray-600 mb-4">{category.description}</p>

            {/* Estadísticas */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{category.tramite_count}</div>
                <div className="text-xs text-blue-800">Trámites</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{category.active_requests_count}</div>
                <div className="text-xs text-yellow-800">Activas</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{category.completion_rate.toFixed(1)}%</div>
                <div className="text-xs text-green-800">Éxito</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{category.application_type_count}</div>
                <div className="text-xs text-purple-800">Estimados</div>
              </div>
            </div>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-col space-y-2 ml-4 pointer-events-auto">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit();
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium pointer-events-auto flex row items-center gap-2"
          >
            <Pen /> Editar
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium pointer-events-auto flex row items-center gap-2"
          >
            <Trash /> Eliminar
          </button>
        </div>
      </div>

      {/* Indicador visual cuando se arrastra */}
      {isDraggedOver && (
        <div className="absolute inset-0 border-4 border-dashed border-blue-500 rounded-xl bg-blue-100 bg-opacity-50 flex items-center justify-center">
          <div className="text-2xl font-bold text-blue-600">
            📍 Soltar para reordenar
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedCategoriesPanel;